import { Link, useSearchParams } from "react-router-dom";
import nsmLogo from "@/assets/brand/nsm-logo-white.png";
import nsmWordmark from "@/assets/brand/nsm-wordmark-white.png";
import { LESSONS, COURSE_LABEL, CourseSlug } from "@/data/lessons";

export interface HeaderProps {
  course: string;
  lesson: string;
  videoUrl?: string;
  /** When set, renders prev/next lesson navigation in the same course. */
  courseSlug?: CourseSlug;
  lessonOrder?: string;
}

const ROUTE_FOR: Record<string, string> = {
  "music-theory/14": "/lesson/circle-of-fifths-for-triads",
};
function lessonRoute(courseSlug: string, order: string): string {
  return ROUTE_FOR[`${courseSlug}/${order}`]
    ?? `/lesson/${courseSlug.replace("south-indian-songbook", "south-indian")}-${order}`;
}

export function Header({ course, lesson, videoUrl, courseSlug, lessonOrder }: HeaderProps) {
  const [searchParams] = useSearchParams();
  /** When ?host=lms the companion is embedded inside the LMS iframe.
   *  Hide elements that would navigate the iframe away from the current lesson. */
  const isLmsEmbed = searchParams.get("host") === "lms";

  // Prev / next lookup
  let prevHref: string | null = null;
  let prevTitle: string | null = null;
  let nextHref: string | null = null;
  let nextTitle: string | null = null;
  if (courseSlug && lessonOrder && LESSONS[courseSlug]) {
    const list = LESSONS[courseSlug];
    const i = list.findIndex((l) => l.order === lessonOrder);
    if (i > 0) {
      prevHref = lessonRoute(courseSlug, list[i - 1].order);
      prevTitle = list[i - 1].title;
    }
    if (i >= 0 && i < list.length - 1) {
      nextHref = lessonRoute(courseSlug, list[i + 1].order);
      nextTitle = list[i + 1].title;
    }
  }

  return (
    <header className="sticky top-0 z-30 border-b border-nsm-border bg-nsm-bg/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 lg:px-8">
        <div className="flex items-center gap-3">
          {/* NSM logo → home */}
          <Link to="/" title="All courses · home" className="-m-1 rounded-full p-1 transition hover:bg-nsm-card">
            <img src={nsmLogo} alt="NSM home" className="h-9 w-auto" />
          </Link>
          <div className="hidden h-8 w-px bg-nsm-border sm:block" />
          <div className="hidden sm:block">
            <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-amber-400">
              {courseSlug ? (
                <Link to="/" className="hover:underline">{course}</Link>
              ) : (
                course
              )}
            </div>
            <div className="font-display text-base font-semibold leading-tight text-nsm-ink">
              {lesson}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Prev / Next — hidden when inside LMS iframe to prevent state drift */}
          {!isLmsEmbed && prevHref && (
            <Link
              to={prevHref}
              title={prevTitle ?? ""}
              className="nsm-btn-ghost text-xs"
            >
              ← Prev
            </Link>
          )}
          {!isLmsEmbed && nextHref && (
            <Link
              to={nextHref}
              title={nextTitle ?? ""}
              className="nsm-btn-ghost text-xs"
            >
              Next →
            </Link>
          )}
          {/* ⌂ home — hidden in LMS (would navigate iframe to sales page) */}
          {!isLmsEmbed && courseSlug && (
            <Link to="/" className="nsm-btn-ghost text-xs" title="All courses">
              ⌂
            </Link>
          )}
          {/* Watch link — hidden in LMS (pops raw video URL in new tab) */}
          {!isLmsEmbed && videoUrl && (
            <a
              href={videoUrl}
              target="_blank"
              rel="noreferrer"
              className="nsm-btn-ghost text-xs"
              title="Open original video lesson"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              Watch
            </a>
          )}
          <img src={nsmWordmark} alt="Nathaniel School of Music" className="hidden h-7 w-auto opacity-80 md:block" />
        </div>
      </div>
    </header>
  );
}
